'use client';

import { useState } from 'react';
import { Job } from '@/Types/JobTypes';
import { MapPin, Briefcase, Calendar, DollarSign, X } from 'lucide-react';
import { jobService } from '@/services/jobService';
import JobApplicationModal from './JobApplicationModal';

interface JobDetailPanelProps {
  job: Job | null;
  onClose?: () => void;
}

export default function JobDetailPanel({ job, onClose }: JobDetailPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!job) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
        <div className="text-center text-gray-500">
          <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">Select a position to view details</p>
          <p className="text-sm mt-2">Click on any job card to see more information</p>
        </div>
      </div>
    );
  }

  const isOpen = jobService.isJobOpen(job);
  const daysUntilClosing = jobService.getDaysUntilClosing(job);
  const salaryRange = jobService.formatSalaryRange(job);

  return (
    <>
      <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-white p-6 relative">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          
          <div className="pr-12">
            <h2 className="text-[28px] font-semibold mb-2">{job.jobTitle}</h2>
            {job.department && (
              <p className="text-secondary text-[16px] font-medium">{job.department}</p>
            )}
          </div>

          {/* Status Badge */}
          <div className="mt-4">
            <span className={`
              inline-block px-4 py-1.5 rounded-full text-[13px] font-medium
              ${isOpen 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
              }
            `}>
              {isOpen ? '● Accepting Applications' : '● Position Closed'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Quick Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Location */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-secondary/10 rounded-lg">
                <MapPin className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-[12px] text-gray-500 uppercase font-medium">Location</p>
                <p className="text-[15px] text-gray-900 font-medium">{job.jobLocation}</p>
              </div>
            </div>

            {/* Job Type */}
            {job.jobType && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-secondary/10 rounded-lg">
                  <Briefcase className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-[12px] text-gray-500 uppercase font-medium">Job Type</p>
                  <p className="text-[15px] text-gray-900 font-medium">{job.jobType}</p>
                </div>
              </div>
            )}

            {/* Salary */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-secondary/10 rounded-lg">
                <DollarSign className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-[12px] text-gray-500 uppercase font-medium">Salary Range</p>
                <p className="text-[15px] text-gray-900 font-medium">{salaryRange}</p>
              </div>
            </div>

            {/* Closing Date */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-secondary/10 rounded-lg">
                <Calendar className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-[12px] text-gray-500 uppercase font-medium">Application Deadline</p>
                <p className={`text-[15px] font-medium ${
                  daysUntilClosing <= 7 && isOpen ? 'text-orange-600' : 'text-gray-900'
                }`}>
                  {isOpen && (
                    <span className="text-[13px] text-gray-600">
                      ({daysUntilClosing} day{daysUntilClosing !== 1 ? 's' : ''} left)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="border-gray-200 my-6" />

          {/* Job Details Sections */}
          <div className="space-y-6">
            {/* Responsibilities */}
            <div>
              <h3 className="text-[18px] font-semibold text-primary mb-3">
                Key Responsibilities
              </h3>
              <div className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                {job.responsibilities}
              </div>
            </div>

            {/* Experience */}
            <div>
              <h3 className="text-[18px] font-semibold text-primary mb-3">
                Required Experience
              </h3>
              <div className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                {job.experience}
              </div>
            </div>

            {/* Education & Certifications */}
            <div>
              <h3 className="text-[18px] font-semibold text-primary mb-3">
                Education & Certifications
              </h3>
              <div className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                {job.educationAndCertifications}
              </div>
            </div>

            {/* Meta Description (if exists) */}
            {job.metaDescription && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-[14px] text-gray-600 italic">
                  {job.metaDescription}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer CTA */}
        {isOpen && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="w-full py-4 bg-secondary text-black font-semibold rounded-lg hover:bg-secondary/90 transition-colors text-[16px]"
            >
              Apply for this Position
            </button>
          </div>
        )}
      </div>

      {/* Application Modal */}
      <JobApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        jobTitle={job.jobTitle}
        jobId={job._id}
      />
    </>
  );
}